/**
 * Platform branding hooks — Wave 1c W1c-11.
 *
 * TanStack Query hooks for the sysadmin-scoped branding endpoints:
 *   GET / PUT /api/v1/admin/platform/branding              (platform default)
 *   GET / PUT /api/v1/admin/platform/tenants/:id/branding  (per-tenant override)
 *   PUT       /api/v1/admin/platform/tenants/:id/branding/locks
 *
 * The sysadmin token (audience argusai-admin) flows via the apiClient.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import type { EffectiveBranding, BrandingColors } from './use-branding';

// ─── Types ──────────────────────────────────────────────────────────────

export interface PlatformBranding {
  id: string;
  portalTitle: string | null;
  displayMode: 'full' | 'both' | 'icon-name';
  lightTheme: Record<string, string>;
  darkTheme: Record<string, string>;
  logoFullAssetId: string | null;
  logoIconAssetId: string | null;
  faviconAssetId: string | null;
  loginBackgroundAssetId: string | null;
  footerText: string | null;
  effectiveVersion: number;
  lockedFields: string[];
  updatedAt: string;
}

export interface PlatformBrandingWrite {
  portalTitle?: string | null;
  displayMode?: 'full' | 'both' | 'icon-name';
  lightTheme?: Partial<BrandingColors>;
  darkTheme?: Partial<BrandingColors>;
  logoFullAssetId?: string | null;
  logoIconAssetId?: string | null;
  faviconAssetId?: string | null;
  loginBackgroundAssetId?: string | null;
  footerText?: string | null;
}

export interface SysadminTenantBrandingResponse {
  effective: EffectiveBranding;
  tenantOverride: {
    portalTitle: string | null;
    displayMode: string;
    lightTheme: Record<string, string>;
    darkTheme: Record<string, string>;
    logoFullAssetId: string | null;
    logoIconAssetId: string | null;
    faviconAssetId: string | null;
    loginBackgroundAssetId: string | null;
    footerText: string | null;
    effectiveVersion: number;
    lockedFields: string[];
  } | null;
  platformDefault: PlatformBranding;
  lockedFields: string[];
}

export interface PutLocksResponse {
  lockedFields: string[];
  added: string[];
  removed: string[];
}

// ─── Platform default ───────────────────────────────────────────────────

export function usePlatformBranding() {
  return useQuery<PlatformBranding, Error>({
    queryKey: ['platform-branding'],
    queryFn: () => apiClient.get<PlatformBranding>('/admin/platform/branding'),
  });
}

export function useUpdatePlatformBranding() {
  const qc = useQueryClient();
  return useMutation<PlatformBranding, Error, PlatformBrandingWrite>({
    mutationFn: (body) =>
      apiClient.put<PlatformBranding>('/admin/platform/branding', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-branding'] });
    },
  });
}

// ─── Per-tenant sysadmin override ───────────────────────────────────────

export function useSysadminTenantBranding(tenantId: string) {
  return useQuery<SysadminTenantBrandingResponse, Error>({
    queryKey: ['sysadmin-tenant-branding', tenantId],
    queryFn: () =>
      apiClient.get<SysadminTenantBrandingResponse>(
        `/admin/platform/tenants/${tenantId}/branding`
      ),
    enabled: !!tenantId,
  });
}

export function useUpdateSysadminTenantBranding(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<EffectiveBranding, Error, Record<string, unknown>>({
    mutationFn: (body) =>
      apiClient.put<EffectiveBranding>(
        `/admin/platform/tenants/${tenantId}/branding`,
        body
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sysadmin-tenant-branding', tenantId] });
    },
  });
}

export function useUpdateTenantBrandingLocks(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<PutLocksResponse, Error, { lockedFields: string[] }>({
    mutationFn: (body) =>
      apiClient.put<PutLocksResponse>(
        `/admin/platform/tenants/${tenantId}/branding/locks`,
        body
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sysadmin-tenant-branding', tenantId] });
    },
  });
}
